import Vue from 'vue'
import VueRouter from 'vue-router'
import { Message } from 'element-ui'
import { chemicalRoutes } from '@/modules/chemical/router'
import { wasteRoutes } from '@/modules/waste/router'
import { shopRoutes } from '@/modules/shop/router'
import { resourceRoutes } from '@/modules/resource/router'

Vue.use(VueRouter)


// ============ 解决 Vue Router 3.x 导航重复或重定向导致的 Promise 报错 ============
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err)
}
// ============ 1. 公共路由（不需要权限，初始化时直接挂载） ============
export const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/account/Login.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/account/ResetPassword.vue')
  },
  {
    path: '/set-new-password',
    name: 'SetNewPassword',
    component: () => import('@/views/account/SetNewPassword.vue')
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue')
  }
]

// ============ 2. 初始化路由实例（仅包含公共路由） ============
// 使用 hash 模式：部署在 域名/代码标识/ 这类子路径下时，# 之后的内容不会发给 nginx，
// 路由不依赖部署路径，静态资源用相对 publicPath 即可随目录迁移。
// 注意：hash 模式不要再配 base，否则地址会变成 #/代码标识/xxx 双重前缀。
const router = new VueRouter({
  mode: 'hash',
  routes: constantRoutes
})

// ============ 3. 动态路由生成逻辑 ============
// 本地定义的所有模块基础配置
const localModuleRoutes = {
  chemical: {
    path: 'chemical',
    component: () => import('@/modules/chemical/layout/index.vue'),
    redirect: '/chemical/inventory',
    meta: { module: 'chemical' },
    children: chemicalRoutes
  },
  waste: {
    path: 'waste',
    component: () => import('@/modules/waste/layout/index.vue'),
    redirect: '/waste/inventory',
    meta: { module: 'waste' },
    children: wasteRoutes
  },
  shop: {
    path: 'shop',
    component: () => import('@/modules/shop/layout/index.vue'),
    redirect: '/shop/main',
    meta: { module: 'shop' },
    children: shopRoutes
  },
  resource: {
    path: 'resource',
    component: () => import('@/modules/resource/layout/index.vue'),
    redirect: '/resource/main',
    meta: { module: 'resource' },
    children: resourceRoutes
  }
}

/**
 * 根据后端菜单过滤本地路由
 * @param {Array} menus 后端返回的菜单列表，假设包含 path 字段，如 ['/chemical/inventory']
 */
export function generateAsyncRoutes(menus) {
  // 提取后端菜单的 code 和 path 集合，仅用于 chemical 模块过滤
  const menuCodes = new Set(menus.map(menu => menu.code).filter(Boolean))
  const menuPaths = new Set(menus.map(menu => menu.path).filter(Boolean))

  const asyncChildren = []

  // 遍历本地模块 (chemical, waste, shop, resource)
  Object.keys(localModuleRoutes).forEach(moduleKey => {
    const moduleRoute = localModuleRoutes[moduleKey]
    let finalChildren = []

    if (moduleKey === 'chemical') {
      // 1. chemical 模块：需要根据菜单权限严格过滤
      finalChildren = moduleRoute.children.filter(child => {
        const fullPath = `/${moduleKey}/${child.path}`
        // 匹配规则：code 匹配 name，或 path 匹配完整路径/相对路径
        return menuCodes.has(child.name) || menuPaths.has(fullPath) || menuPaths.has(child.path)
      })
    } else {
      // 2. waste 等其他模块：不需要过滤，直接保留所有子路由
      finalChildren = [...moduleRoute.children]
    }

    // 如果该模块有子路由，则注入到异步路由中
    if (finalChildren.length > 0) {
      asyncChildren.push({
        ...moduleRoute,
        // 【关键】动态重定向到该模块下第一个可用的子路由，避免写死 redirect 导致 404
        redirect: `/${moduleKey}/${finalChildren[0].path}`,
        children: finalChildren
      })
    }
  })

  // 构建主布局路由
  const mainLayoutRoute = {
    path: '/',
    component: () => import('@/layout/MainLayout.vue'),
    // 动态重定向到第一个有权限/可用的模块 (例如 /chemical 或 /waste)
    redirect: asyncChildren.length > 0 ? `/${asyncChildren[0].path}` : '/404',
    meta: { requiresAuth: true },
    children: asyncChildren
  }
  console.log('mainLayoutRoute',mainLayoutRoute)
  return [mainLayoutRoute]
}
// 标记是否已经添加了动态路由
let hasAddedRoutes = false

/**
 * 重置路由（用于退出登录时清空动态路由）
 */
export function resetRouter() {
  hasAddedRoutes = false
  const newRouter = new VueRouter({
    mode: 'hash',
    routes: constantRoutes
  })
  // Vue Router 3.x 重置路由的 hack 方法
  router.matcher = newRouter.matcher
}

// ============ 4. 路由守卫 ============
router.beforeEach(async (to, from, next) => {
  const token = window.tokenCache || localStorage.getItem('token')

  // 1. 目标已是登录页：直接放行，并重置路由状态
  if (to.path === '/login') {
    if (hasAddedRoutes) resetRouter()
    next()
    return
  }

  // 2. 无 Token：拦截并跳转登录
  if (!token) {
    if (to.path !== '/404') Message.warning('请先登录')
    next('/login')
    return
  }

  // 3. 有 Token 但未加载动态路由：获取菜单并动态添加
  if (!hasAddedRoutes) {
    try {
      // 注意：如果 StorageUtil 是异步的（如 IndexedDB），这里需要 await StorageUtil.getItem('menus')
      const menusStr = localStorage.getItem('menus')
      const menus = menusStr ? JSON.parse(menusStr) : []


      const asyncRoutes = generateAsyncRoutes(menus)
      console.log('asyncRoutes', asyncRoutes)
      // 动态添加路由，注意 404 通配符必须放在最后
      router.addRoutes([
        ...asyncRoutes,
        { path: '*', name: 'GlobalNotFound', redirect: '/404', hidden: true }
      ])

      hasAddedRoutes = true

      // 重新触发一次路由跳转，确保 addRoutes 生效（带 replace: true 避免死循环）
      next({ ...to, replace: true })
    } catch (error) {
      console.error('获取菜单失败', error)
      localStorage.removeItem('token')
      next('/login')
    }
  } else {
    // 4. 已加载动态路由：正常放行
    next()
  }
})

// ============ 5. 路由后置守卫：动态切换 body 主题 class ============
router.afterEach((to) => {
  const module = to.matched.find(record => record.meta.module)?.meta.module
  document.body.classList.remove('theme-chemical', 'theme-waste', 'theme-shop', 'theme-resource')
  if (module) {
    document.body.classList.add(`theme-${module}`)
  }
})

export default router